import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Project } from '@ops/shared-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectStatusGuardService {
  private currentProject$ = new BehaviorSubject<Project | null>(null);
  private projectStatusPrevention$ = new BehaviorSubject<boolean>(false);
  private projectStatusFullPrevention$ = new BehaviorSubject<boolean>(false);

  /**
   * Set the current project and determine if prevention should be applied
   * @param project The current project
   */
  setCurrentProject(project: Project | null): void {
    this.currentProject$.next(project);
    this.updatePreventionStatus(project);
  }

  /**
   * Get the current project
   */
  getCurrentProject(): Project | null {
    return this.currentProject$.value;
  }

  /**
   * Get current project as observable
   */
  getCurrentProject$(): Observable<Project | null> {
    return this.currentProject$.asObservable();
  }

  /**
   * Check if project status prevention should be applied
   * @param project The project to check
   * @returns true if prevention should be applied (status >= 2)
   */
  shouldApplyPrevention(project: Project | null): boolean {
    if (!project || !project.status) {
      return false;
    }
    
    const status = parseInt(project.status, 10);
    return status >= 2; // Completed (2) and Submitted (3) should prevent interactions
  }

  /**
   * Check if project status is less than or equal to (Completed) 2 (enabled comments)
   * @param project The project to check
   * @returns true if status <= 2
   */
  shouldEnableOnlyCommentsFeature(project: Project | null): boolean {
    if (!project || !project.status) {
      return false;
    }
    
    const status = parseInt(project.status, 10);
    return status === 2; // Completed (2) and Submitted (3) should enable comments
  }


  /**
   * Check if project status is greater than 2 (full prevention)
   * @param project The project to check
   * @returns true if status > 2
   */
  shouldApplyFullPrevention(project: Project | null): boolean {
    if (!project || !project.status) {
      // console.log('shouldApplyFullPrevention: No project or status', { project, status: project?.status });
      return false;
    }
    
    const status = parseInt(project.status, 10);
    const result = status > 2;
    return result; // Only status > 2 (Submitted (3) and above)
  }

  /**
   * Get prevention status as observable
   */
  getProjectStatusPrevention$(): Observable<boolean> {
    return this.projectStatusPrevention$.asObservable();
  }

  /**
   * Get current prevention status
   */
  getProjectStatusPrevention(): boolean {
    return this.projectStatusPrevention$.value;
  }

  /**
   * Get full prevention status as observable
   */
  getProjectStatusFullPrevention$(): Observable<boolean> {
    return this.projectStatusFullPrevention$.asObservable();
  }

  /**
   * Get current full prevention status
   */
  getProjectStatusFullPrevention(): boolean {
    return this.projectStatusFullPrevention$.value;
  }

  /**
   * Manual trigger to update prevention status
   */
  forceUpdatePreventionStatus(): void {
    // console.log('Project Status Guard - Force updating prevention status');
    this.updatePreventionStatus(this.currentProject$.value);
  }

  /**
   * Test full prevention logic with specific status
   */
  testFullPreventionWithStatus(status: string): void {
    const testProject = { 
      _id: 'test', 
      name: 'Test Project', 
      status 
    } as Project;
    
    // console.log('Testing Full Prevention with Status:', status);
    this.setCurrentProject(testProject);
  }

  /**
   * Test status change flow from 2 to 3
   */
  testStatusChangeFlow(): void {
    // console.log('=== Testing Status Change Flow ===');
    
    // Start with status 2
    const projectStatus2 = { 
      _id: 'test', 
      name: 'Test Project', 
      status: '2' 
    } as Project;
    
    // console.log('1. Setting initial status to 2');
    this.setCurrentProject(projectStatus2);
    
    // Change to status 3
    const projectStatus3 = { 
      _id: 'test', 
      name: 'Test Project', 
      status: '3' 
    } as Project;
    
    // console.log('2. Changing status to 3');
    this.setCurrentProject(projectStatus3);
    
    // console.log('=== Status Change Flow Test Complete ===');
  }

  /**
   * Update prevention status based on current project
   */
  private updatePreventionStatus(project: Project | null): void {
    const shouldPrevent = this.shouldApplyPrevention(project);
    const shouldFullPrevent = this.shouldApplyFullPrevention(project);
    
    this.projectStatusPrevention$.next(shouldPrevent);
    this.projectStatusFullPrevention$.next(shouldFullPrevent);
    
    
  }

  /**
   * Get project status name for display
   */
  getProjectStatusName(project: Project | null): string {
    if (!project || !project.status) {
      return 'Unknown';
    }

    const statusMap: { [key: number]: string } = {
      0: 'Pitching',
      1: 'Ongoing', 
      2: 'Completed',
      3: 'Submitted',
      4: 'Cancelled'
    };

    const status = parseInt(project.status, 10);
    return statusMap[status] || 'Unknown';
  }

  /**
   * Check if project is in read-only state (status >= 2)
   */
  isProjectReadOnly(project: Project | null): boolean {
    return this.shouldApplyPrevention(project);
  }

}
